import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { SCARPING_TYP, AUTH_TYPE } from '@constants/codeConstants';
import { ext } from '@api/axiosAPI';
import AnalysisGuideTemplate from './AnalysisGuideTemplate';
import AuthInfoTemplate from './AuthInfoTemplate';
import AuthWaitTemplate from './AuthWaitTemplate';
import AuthComplateTemplate from './AuthComplateTemplate';
import LoadingComponent from '@components/common/LoadingComponent';
import BottomButtonGroup from '@components/common/BottomButtonGroup';

const AuthRequestTemplate = () => {
  const location = useLocation();
  const { reqCd } = useParams();
  const queryStringParams = new URLSearchParams(location.search);
  const [userId, careObjUserId, authReqSrno] = (queryStringParams.get('data') ?? '').split(';');
  const [userInfo, setUserInfo] = useState({}); // 유저정보
  const [step, setStep] = useState(1); // 단계
  const [checkedItems, setCheckedItems] = useState([]); // 약관 동의 상태 유지
  const [isSuccess, setIsSuccess] = useState(null); // 간편인증성공여부 true: 성공, false: 실패
  const [sessionId, setSessionId] = useState(''); // 세션 ID
  const [isLoading, setIsLoading] = useState(false);
  const [isServerError, setIsServerError] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  // const isHealthCheck = reqCd === SCARPING_TYP.MECHUP; // 타입체크 (건강검진, 처방전)
  const [isNhis, setIsNhis] = useState(true);
  const [scrapingTyp, setScrapingTyp] = useState('');

  useEffect(() => {
    setScrapingTyp(reqCd);
  }, [reqCd]);

  useEffect(() => {
    if (scrapingTyp === SCARPING_TYP.HIRA_MEDICATION) {
      setIsNhis(false);
    } else {
      setIsNhis(true);
    }
  }, [scrapingTyp]);

  // 유저 정보 API 호출
  const { isLoading: userLoading, data: userData } = ext.getUserInfo(
    ['getUserInfo', careObjUserId],
    { enabled: true, staleTime: 0, cacheTime: 0 },
    { userId: careObjUserId }
  );

  // 가족 관계 정보 API 호출
  const { isLoading: familiyCareLoading, data: familiyData } = ext.getFamilyCareRelInfo(
    ['getFamilyCareRelInfo', careObjUserId],
    { enabled: true, staleTime: 0, cacheTime: 0 },
    { userId, careObjUserId }
  );

  const filteredLegalData = isNhis ? legalData.filter((item) => ['1', '3'].includes(item.legalSrno)) : legalData;

  const getUserSexCd = (ssnBack) => {
    if (!ssnBack) return null;

    ssnBack = ssnBack + '';
    if (ssnBack?.charAt(0) === '1' || ssnBack?.charAt(0) === '3') {
      return '009001';
    } else {
      return '009002';
    }
  };

  /* 배경색 적용 */
  useEffect(() => {
    document.body.classList.add('bg-f6f6f6');
    return () => document.body.classList.remove('bg-f6f6f6');
  }, []);

  /* 유저정보 세팅 */
  useEffect(() => {
    if (userData) setUserInfo(userData);
  }, [userData]);

  /* step2로 돌아온 순간 loading,sessionId 정보 초기화 */
  useEffect(() => {
    if (step === 2) {
      console.log('성공여부 초기화');
      setIsSuccess(null);
      setSessionId('');
      setIsServerError(false);
    }
  }, [step]);

  /**
   * 사용자 정보 수정
   */
  const putUserInfo = ext.putUserInfo(
    {
      onSuccess: (res) => {
        console.log('사용자 정보 수정 성공.');
      },
      onError: (err) => {
        console.log('사용자 정보 수정 실패');
      },
    },
    {
      careObjUserId: careObjUserId,
      userNm: userInfo.userNm,
      userBod: isNhis ? userInfo.birthDate : userInfo.userBod,
      userTlno: userInfo.userTlno,
      sexCd: !isNhis ? getUserSexCd(userInfo.ssnBack) : null,
    }
  );

  /* 건강보험공단 간편인증 */
  const nhisAuthFn = ext.postAuthgNhis(
    {
      onSuccess: (res) => {
        setIsLoading(false);
        setSessionId(res.sessionId);
        console.log('건강보험공단 간편인증 성공. sessionId: ', res.sessionId);
      },
      onError: (err) => {
        setIsLoading(false);
        setIsSuccess(false);
        console.log('건강보험공단 간편인증 실패');
      },
    },
    {
      userId: userId,
      scrapingType: scrapingTyp === SCARPING_TYP.HELT ? SCARPING_TYP.HELT : SCARPING_TYP.MECHUP,
      authType: AUTH_TYPE.KAKAO,
      userNm: userInfo.userNm,
      userBod: userInfo.birthDate,
      userTlno: userInfo.userTlno,
    }
  );

  /* 심평원 간편인증 */
  const hiraAuthFn = ext.postAuthHira(
    {
      onSuccess: (res) => {
        setIsLoading(false);
        setSessionId(res.sessionId);
        console.log('처방전 간편인증 성공. sessionId: ', res.sessionId);
      },
      onError: () => {
        setIsLoading(false);
        setIsSuccess(false);
        console.log('처방전 간편인증 실패');
      },
    },
    {
      scrapingType: SCARPING_TYP.HIRA_MEDICATION,
      authType: AUTH_TYPE.KAKAO,
      userNm: userInfo.userNm,
      frstJuminNum: userInfo.ssnFront,
      lastJuminNum: userInfo.ssnBack,
      userTlno: userInfo.userTlno,
    }
  );

  /* 건강검진 스크래핑 */
  const nhisScrapFn = ext.postScrapingNhis(
    {
      onSuccess: (res) => {
        setIsSuccess(true);
        goNextStep(true);
        setIsLoading(false);
        console.log('건강검진 스크래핑 성공');
        successAuthInfo.mutate();
        putUserInfo.mutate();
      },
      onError: (err) => {
        setIsSuccess(false);
        setIsLoading(false);
        if (err?.resultCode === 'SERVICE_UNAVAILABLE') {
          setIsServerError(true);
        }
        console.log('건강검진 스크래핑 실패', err);
      },
    },
    {
      userId,
      careObjUserId,
      sessionId,
    }
  );

  /* 가족 처방 스크래핑 */
  const hiraScrapFn = ext.postScrapingHira(
    {
      onSuccess: (res) => {
        setIsSuccess(true);
        goNextStep(true);
        setIsLoading(false);
        console.log('처방전 스크래핑 성공');
        successAuthInfo.mutate();
        putUserInfo.mutate();
      },
      onError: (err) => {
        setIsSuccess(false);
        setIsLoading(false);
        if (err?.resultCode === 'SERVICE_UNAVAILABLE') {
          setIsServerError(true);
        }
        console.log('처방전 스크래핑 실패', err);
      },
    },
    {
      userId,
      sessionId,
      careObjUserId,
    }
  );
  /* 건강예측 스크래핑 */
  const heltScrapFn = ext.postScrapingHelt(
    {
      onSuccess: (res) => {
        setIsSuccess(true);
        goNextStep(true);
        setIsLoading(false);
        console.log('건강예측 스크래핑 성공');
        successAuthInfo.mutate();
        putUserInfo.mutate();
      },
      onError: (err) => {
        setIsSuccess(false);
        setIsLoading(false);
        if (err?.resultCode === 'SERVICE_UNAVAILABLE') {
          setIsServerError(true);
        }
        console.log('건강예측 스크래핑 실패', err);
      },
    },
    {
      userId,
      careObjUserId,
      sessionId,
    }
  );

  // 인증 요청 성공
  const successAuthInfo = ext.createExternalAuthInfo(
    {
      onSuccess: (res) => {
        console.log('modifyExternalAuthInfo 호출 성공:', res);
      },
      onError: (err) => {
        console.error('modifyExternalAuthInfo 호출 실패:', err);
      },
    },
    {
      authReqUserId: userId,
      authObjUserId: careObjUserId,
      authReqSrno,
      authProcDivCd: '031002',
    }
  );

  /* 페이지 이동 */
  const goNextStep = useCallback((isNext = true) => {
    setStep((prevStep) => (isNext ? prevStep + 1 : prevStep - 1));
  }, []);

  /* 헤더 */
  const HealthUpHeader = ({ step }) => (
    <section className="back_subheader">
      <h1 className="Width-100 fs-20 align-c">
        {step === 1 ? <img src="/assets/images/healthup_bi_bk.svg" className="hd_bi" alt="Logo" /> : <span className="fw-b">간편인증 본인확인</span>}
      </h1>
    </section>
  );

  /* 공통 바텀버튼 제어 */
  const getButtonsForStep = (step) => {
    switch (step) {
      case 1:
        return [
          {
            label: '인증 시작',
            onClick: () => goNextStep(true),
            className: '',
            disabled: false,
            description: (
              <ul className="info_txt_box">
                {scrapingTyp && scrapingTyp === SCARPING_TYP.MECHUP && <li>건강검진 연동을 위한 건강in 사이트 간편 인증입니다.</li>}
                {scrapingTyp && scrapingTyp === SCARPING_TYP.HIRA_MEDICATION && <li>처방전 수집을 위한 건강보험심사평가원 인증입니다.</li>}
                {scrapingTyp && scrapingTyp === SCARPING_TYP.HELT && <li>건강예측 연동을 위한 건강in 사이트 간편 인증입니다.</li>}
                <li>잘못된 인증 요청인 경우 무시해 주세요.</li>
                <li>인증은 3분 이내 완료되어야 합니다.</li>
                <li>시간이 지난 경우 다시 요청하세요.</li>
              </ul>
            ),
          },
        ];
      case 2:
        return [
          {
            label: '본인 인증',
            onClick: handleNextStep,
            className: checkedItems.length !== filteredLegalData.length ? 'bg-ccc' : '',
            disabled: checkedItems.length !== filteredLegalData.length,
          },
        ];
      case 3:
        return [
          { label: '다시 요청', onClick: () => goNextStep(false), className: 'mr-10 bg-ccc font-white' },
          {
            label: '인증 완료',
            onClick: () => {
              setIsLoading(true);

              if (scrapingTyp === SCARPING_TYP.MECHUP) {
                nhisScrapFn.mutate();
              } else if (scrapingTyp === SCARPING_TYP.HIRA_MEDICATION) {
                hiraScrapFn.mutate();
              } else if (scrapingTyp === SCARPING_TYP.HELT) {
                heltScrapFn.mutate();
              }
            },
          },
        ];
      case 4:
        return [];
      default:
        return [];
    }
  };

  /* step2 validation check */
  const validateCheck = (userInfo, scrapingTyp) => {
    const invalidFields = [];
    const nameRegex = /^[가-힣]+$/;
    if (!nameRegex.test(userInfo?.userNm)) invalidFields.push('userNm');

    if (scrapingTyp === SCARPING_TYP.MECHUP || scrapingTyp === SCARPING_TYP.HELT) {
      const birthDateRegex = /^[0-9]{8}$/;
      if (!birthDateRegex.test(userInfo?.birthDate)) invalidFields.push('birthDate');
    } else {
      const ssnFrontRegex = /^[0-9]{6}$/;
      if (!ssnFrontRegex.test(userInfo?.ssnFront)) invalidFields.push('ssnFront');

      const ssnBackRegex = /^[1-4][0-9]{6}$/;
      if (!ssnBackRegex.test(userInfo?.ssnBack)) invalidFields.push('ssnBack');
    }

    const phoneRegex = /^(\d{3})(\d{4})(\d{4})$/;
    if (!phoneRegex.test(userInfo?.userTlno)) invalidFields.push('userTlno');

    return invalidFields;
  };

  /* step2에서 바텀 버튼 클릭 이벤트 */
  const handleNextStep = () => {
    const result = validateCheck(userInfo, scrapingTyp);
    if (result.length === 0) {
      setIsLoading(true);

      if (scrapingTyp === SCARPING_TYP.MECHUP) {
        nhisAuthFn.mutate();
      } else if (scrapingTyp === SCARPING_TYP.HIRA_MEDICATION) {
        hiraAuthFn.mutate();
      } else if (scrapingTyp === SCARPING_TYP.HELT) {
        nhisAuthFn.mutate();
      }
      goNextStep(true);
    } else {
      console.log('Validation failed for fields:', result);
    }
    setInvalidFields(result);
  };

  if (userLoading || familiyCareLoading || !userInfo) return null;

  return (
    <>
      <HealthUpHeader step={step} />
      {step === 1 && <AnalysisGuideTemplate userInfo={userInfo} familiyData={familiyData} />}
      {step === 2 && (
        <AuthInfoTemplate
          isNhis={isNhis}
          scrapingTyp={scrapingTyp}
          legalData={filteredLegalData}
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          checkedItems={checkedItems}
          setCheckedItems={setCheckedItems}
          invalidFields={invalidFields}
        />
      )}
      {step === 3 && <AuthWaitTemplate isSuccess={isSuccess} isServerError={isServerError} goNextStep={goNextStep} />}
      {step === 4 && <AuthComplateTemplate />}
      <BottomButtonGroup buttons={getButtonsForStep(step)} step={step} />
      <LoadingComponent isLoading={isLoading} />
    </>
  );
};

export default AuthRequestTemplate;

/** 약관 */
const legalData = [
  {
    legalSrno: '1',
    legalDesc: '개인정보 이용 동의',
  },
  {
    legalSrno: '2',
    legalDesc: '고유식별 정보 처리 동의',
  },
  {
    legalSrno: '3',
    legalDesc: '제3자 정보 제공 동의',
  },
];
